// ----------------------------------------------------------------------
// <copyright file="Izenda.FilterPropertiesSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import { merge } from 'lodash/fp';
import localizer from 'share/localization/Izenda.Localizer';

const filterOperator = {
  component: 'Panel',
  staticProps: {
    initState: true,
    collapsible: true,
    header: {
      get title() {
        return localizer.getText('FILTER_PROPERTIES_SCHEMA_LABEL_FILTER_OPERATOR');
      },
      icon: 'icon-toggle-triangle'
    },
    className: 'u_fullWidth'
  },
  props: {
    disabled: 'isFilterAliasDisabled'
  },
  fields: {
    filterDetailOperator: {
      component: 'OperatorGroup',
      usingGroup: true,
      props: {
        configField: 'configField',
        updateSourceValue: 'updateSourceValue',
        reportFilters: 'reportFilters',
        store: 'fieldStore',
        isLazyLoading: true,
        disabled: 'isFilterAliasDisabled',
        isPopup:'isPopup'
      }
    },
    useLookup: {
      type: 'checkbox',
      fnValue: 'getValueByKey',
      get title() {
        return localizer.getText('FILTER_PROPERTIES_SCHEMA_LABEL_USE_LOOKUP');
      },
      isHidden: 'isHiddenUseLookup'
    }
  }
};

export default {
  schemaKey: 'FILTER_PROPERTIES',
  fields: {
    sourceInfo: {
      component: 'Panel',
      props: {
        disabled: 'isFilterAliasDisabled'
      },
      staticProps: {
        initState: true,
        collapsible: true,
        header: {
          title: 'Source',
          icon: 'icon-toggle-triangle'
        },
        className: 'u_fullWidth'
      },

      fields: {
        querySourceType: {
          get title() {
            return localizer.getText('COMMON_LABEL_TYPE');
          },
          isHidden: 'isAggregatedField',
          type: 'static',
          fnValue: 'getValueByKey'
        },

        sourceDataObjectName: {
          get title() {
            return localizer.getText('COMMON_LABEL_NAME');
          },
          isHidden: 'isAggregatedField',
          type: 'static',
          fnValue: 'getValueByKey'
        },

        querySourceFieldId: {
          get title() {
            return localizer.getText('FILTER_PROPERTIES_SCHEMA_LABEL_FILTER_NAME');
          },
          isHidden: 'isAggregatedField',
          fnValue: 'getQuerySourceValue',
          type: 'static'
        },

        reportPartTitle: {
          get title() {
            return localizer.getText('COMMON_LABEL_REPORT_PART');
          },
          isHidden: 'isPhysicalField',
          type: 'static',
          fnValue: 'getValueByKey'
        },

        querySourceAggregatedField: {
          get title() {
            return localizer.getText('COMMON_LABEL_FIELD_NAME');
          },
          isHidden: 'isPhysicalField',
          fnValue: 'getQuerySourceValue',
          type: 'static'
        }
      }
    },

    filterOperator,

    filterSettings: {
      component: 'Panel',
      staticProps: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('FILTER_PROPERTIES_SCHEMA_LABEL_FILTER_SETTINGS');
          },
          icon: 'icon-toggle-triangle'
        },
        className: 'u_fullWidth'
      },
      props: {
        disabled: 'isFilterAliasDisabled'
      },
      fields: {
        alias: {
          get title() {
            return localizer.getText('FILTER_PROPERTIES_SCHEMA_LABEL_FILTER_ALIAS');
          },
          type: 'text',
          fnValue: 'getFilterAlias',
          props: {
            type: 'filterAliasInputType',
            disabled: 'isFilterAliasDisabled',
            isMasked: 'isFilterAliasDisabled',
            validations: 'getAliasValidation',
            field: 'alias',
            onBlur: 'validateItemValue'
          }
        },
        visible: {
          get title() {
            return localizer.getText('COMMON_LABEL_VISIBLE');
          },
          type: 'checkbox',
          fnValue: 'getValueByKey',
          props: {
            disabled: 'isFilterAliasDisabled'
          }
        },
        required: {
          get title() {
            return localizer.getText('COMMON_CONSTRAINT_REQUIRED');
          },
          type: 'checkbox',
          fnValue: 'getValueByKey',
          props: {
            disabled: 'isFilterAliasDisabled'
          }
        },
        cascading: {
          get title() {
            return localizer.getText('FILTER_PROPERTIES_SCHEMA_LABEL_CASCADING');
          },
          type: 'checkbox',
          fnValue: 'getValueByKey',
          disabledInfo: localizer.getText('COMMON_LABEL_CONTROL_DISABLE_INFO'),
          props: {
            disabled: 'isDisableCascading'
          }
        },
        sortType: {
          get title() {
            return localizer.getText('FILTER_PROPERTIES_SCHEMA_LABEL_SORT');
          },
          component: 'ButtonHighlight',
          fnValue: 'getValueByKey',
          staticProps: {
            defaultIcon: 'icon-unsort'
          },
          props: {
            className: 'without-active',
            loadValue: 'getSortTypeValue',
            propertyKey: 'sortType',
            isEmptyValue: 'isEmptySortValue',
            get description() {
              return localizer.getText('FILTER_PROPERTIES_SCHEMA_LABEL_SORT');
            },
            disabledIcon: 'icon-unsort',
            states: [{ value: 'ASC', icon: 'icon-sort-name-up' }, { value: 'DESC', icon: 'icon-sort-name-down' }]
          }
        }
      }
    },

    filterFormating: {
      component: 'Panel',
      isHidden: 'isFilterFormatingHidden',
      props: {
        disabled: 'isFilterAliasDisabled'
      },
      staticProps: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('FILTER_PROPERTIES_SCHEMA_LABEL_FILTER_FORMATTING');
          },
          icon: 'icon-toggle-triangle'
        },
        className: 'u_fullWidth'
      },

      fields: {
        dataFormatId: {
          get title() {
            return localizer.getText('COMMON_LABEL_FORMAT');
          },
          type: 'select',
          fnValue: 'getValueByKey',
          props: {
            options: 'getDataFormats',
            labelField: 'name',
            valueField: 'id',
            groupBy: 'category',
            showTooltip: true,
            blankOption: true,
            blankOptionText: '...'
          }
        },

        // font family + font size
        fontFormat: {
          get title() {
            return localizer.getText('FONT_SETTINGS_LABEL_FONT');
          },
          component: 'FontFormat',
          fnValue: 'getFontFormat'
        },

        // bold, italic, underline
        fontStyle: {
          title: '',
          component: 'FontStyle',
          fnValue: 'getFontStyle'
        },

        color: {
          get title() {
            return localizer.getText('COMMON_LABEL_COLOR');
          },
          component: 'ColorStyling',
          fnValue: 'getColorStyling',
          props: {
            type: 'photoshop',
            position: 'left',
            disabled: 'isFilterAliasDisabled'
          }
        }
      }
    }
  },
  GlimpseSchema: {
    schemaKey: 'FILTER_PROPERTIES_SIMPLE',
    fields: {
      filterOperator: merge(filterOperator, {
        staticProps: {
          header: null
        },
        fields: {
          filterDetailOperator: {
            props: {
              renderInContainer: false
            }
          },
          useLookup: {
            isHidden: () => true
          }
        }
      })
    }
  }
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.FilterPropertiesSchema.js